/**
 * Buy Box Analysis Logic - Sellerscout Compatible Version
 *
 * This version uses the same data structure as the Sellerscout project
 * for easy integration.
 */

// ============================================
// TYPE DEFINITIONS (Compatible with Sellerscout)
// ============================================

/**
 * Fulfillment types
 */
type TFulfilmentType = "AMZ" | "FBA" | "FBM";

/**
 * Keepa offer structure (from Sellerscout)
 */
export interface TOffer {
  sellerId: string;
  fulfillment: TFulfilmentType;
  stock: number | null;
  price: number | null;
  isPrime: boolean;
}

/**
 * Keepa data structure (from Sellerscout)
 */
export interface KeepaData {
  buyBoxSellerIdHistory: (number | string)[]; // Alternating: [time, sellerId, time, sellerId, ...]
  csv: number[][]; // 2D array: csv[18] for buy box prices
  lastUpdate?: number; // Keepa timestamp (optional)
  offers: TOffer[]; // Current offers
}

/**
 * Buy box analysis output (from Sellerscout)
 */
export interface TBuyBoxAnalysis {
  avgPrice: number;
  won: number;
  lastWon: Date;
  stock: number;
  type: string;
  sellerId: string;
  link: string;
  sellerName?: string; // Display name for seller
}

/**
 * Keepa CSV indices
 */
export enum KeepaCSV {
  AMAZON = 0,
  NEW = 1,
  USED = 2,
  SALES_RANK = 3,
  NEW_FBA = 10,
  NEW_FBM_SHIPPING = 7,
  BUY_BOX_SHIPPING = 18,
}

/**
 * Special seller IDs
 */
const SUPPRESSED_ID = "-1" as const;
const OUT_OF_STOCK_ID = "-2" as const;
const INVALID_ID = "-" as const;
const EMPTY_ID = "" as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert Keepa time (minutes since Keepa epoch) to Unix timestamp (ms)
 */
export function keepaTimeToUnixDateTime(keepaTime: number | string): number {
  return (parseInt(String(keepaTime), 10) + 21564000) * 60000;
}

/**
 * Convert Unix milliseconds to Keepa timestamp
 */
export function unixDateTimeToKeepaTime(unixTime: number): number {
  return Math.floor(unixTime / 60000) - 21564000;
}

/**
 * Check if a seller ID is valid (not a truly invalid ID like "-" or empty)
 * Note: SUPPRESSED (-1) and OUT_OF_STOCK (-2) are valid and should be displayed
 */
function isValidSellerId(sellerId: string | number): boolean {
  if (typeof sellerId !== "string") return false;

  // SUPPRESSED (-1) and OUT_OF_STOCK (-2) are valid special IDs
  if (sellerId === SUPPRESSED_ID || sellerId === OUT_OF_STOCK_ID) {
    return true;
  }

  // Filter out truly invalid IDs
  if (sellerId === INVALID_ID || sellerId === EMPTY_ID) {
    return false;
  }

  // Filter out very short IDs (likely invalid)
  if (sellerId.length < 2) {
    return false;
  }

  return true;
}

/**
 * Get display name for seller ID
 * Matches Selleramp's describeSellerNameByID logic
 */
function getSellerDisplayName(sellerId: string): string {
  if (sellerId === SUPPRESSED_ID) {
    return "SUPPRESSED";
  }
  if (sellerId === OUT_OF_STOCK_ID) {
    return "OUT OF STOCK";
  }
  return sellerId; // Return ID as placeholder (will be replaced by actual name from API)
}

/**
 * Extract unique seller IDs from alternating history [time, sellerId, time, sellerId, ...]
 * Filters out special/invalid IDs
 * If lastUpdate is provided, only returns seller IDs with last won date in past 365 days
 */
export function extractSellerIds(
  buyBoxHistory: (number | string)[],
  lastUpdate: number = unixDateTimeToKeepaTime(Date.now()),
): string[] {
  const sellerIds: string[] = [];
  const allSellerIds: (string | number)[] = [];

  // Extract seller IDs from odd indices
  for (let i = 1; i < buyBoxHistory.length; i += 2) {
    const sellerId = buyBoxHistory[i];
    allSellerIds.push(sellerId);

    if (typeof sellerId === "string" && isValidSellerId(sellerId)) {
      sellerIds.push(sellerId);
    }
  }

  const uniqueSellerIds = Array.from(new Set(sellerIds));

  // If lastUpdate is provided, filter by last won date (past 365 days)
  if (lastUpdate !== undefined) {
    const now = Date.now();
    const oneYearAgo = now - 365 * 24 * 60 * 60 * 1000; // 365 days in milliseconds

    return uniqueSellerIds.filter((sellerId) => {
      const lastWonDate = calculateLastWon(buyBoxHistory, sellerId, lastUpdate);
      return lastWonDate.getTime() >= oneYearAgo;
    });
  }

  return uniqueSellerIds;
}

/**
 * Calculate percentage of time each seller won the buy box (TIME-WEIGHTED)
 * Uses alternating format: [time, sellerId, time, sellerId, ...]
 * Matches Selleramp's deriveBuyBoxHistoryData logic
 */
function calculatePercentWon(
  buyBoxHistory: (number | string)[],
  sellerId: string,
  rangeStart: number,
  lastUpdate: number,
): number {
  if (buyBoxHistory.length < 2) return 0;

  const buyBoxHistoryData: Record<string, number> = {};

  // Iterate through seller IDs (odd indices)
  for (let i = 1; i < buyBoxHistory.length; i += 2) {
    const currentSellerId = buyBoxHistory[i];
    if (typeof currentSellerId !== "string") continue;

    // Initialize if needed
    if (!buyBoxHistoryData[currentSellerId]) {
      buyBoxHistoryData[currentSellerId] = 0;
    }

    // Get timestamps (can be strings or numbers)
    const prevTimestampRaw = buyBoxHistory[i - 1];
    const nextTimestampRaw = buyBoxHistory[i + 1];

    // Convert to numbers
    const prevTimestamp =
      typeof prevTimestampRaw === "string"
        ? parseInt(prevTimestampRaw, 10)
        : prevTimestampRaw;

    const nextTimestamp =
      typeof nextTimestampRaw === "string"
        ? parseInt(nextTimestampRaw, 10)
        : nextTimestampRaw;

    if (typeof prevTimestamp !== "number" || isNaN(prevTimestamp)) continue;

    // Calculate start and end times
    const isFirstIndex = i === 1;
    const isLastIndex = i === buyBoxHistory.length - 1;

    let startTime = isFirstIndex
      ? rangeStart
      : keepaTimeToUnixDateTime(prevTimestamp);

    let endTime = isLastIndex
      ? lastUpdate
      : typeof nextTimestamp === "number" && !isNaN(nextTimestamp)
        ? keepaTimeToUnixDateTime(nextTimestamp)
        : lastUpdate;

    // Handle edge case: if start and end are the same (history length = 2)
    // Use current date/time as end (Selleramp's logic line 102000-2006)
    if (startTime === endTime) {
      endTime = Date.now();
    }

    // Calculate duration
    const duration = endTime - startTime;

    if (duration > 0) {
      buyBoxHistoryData[currentSellerId] += duration;
    }
  }

  // Calculate total time
  const totalTime = Object.values(buyBoxHistoryData).reduce(
    (sum, time) => sum + time,
    0,
  );

  console.log("calculatePercentWon - totalTime:", totalTime);

  if (totalTime === 0) return 0;

  // Calculate percentage for this seller
  const percentage = (buyBoxHistoryData[sellerId] / totalTime) * 100;

  // Round if > 1%, keep decimal if < 1% (Selleramp's logic)
  return percentage > 1 ? Math.round(percentage) : percentage;
}

/**
 * Calculate last won time
 * Uses alternating format: [time, sellerId, time, sellerId, ...]
 * Matches Selleramp's deriveLastWonData logic
 */
function calculateLastWon(
  buyBoxHistory: (number | string)[],
  sellerId: string,
  lastUpdate: number,
): Date {
  const now = keepaTimeToUnixDateTime(lastUpdate);
  let lastWonTime = 0;

  // Find the last occurrence of this seller (odd indices)
  for (let i = buyBoxHistory.length - 1; i >= 1; i -= 2) {
    if (buyBoxHistory[i] === sellerId) {
      // Get the end time for this period
      const isLastIndex = i === buyBoxHistory.length - 1;

      if (isLastIndex) {
        lastWonTime = now;
      } else {
        const nextTimestampRaw = buyBoxHistory[i + 1];
        // Convert to number if it's a string
        const nextTimestamp =
          typeof nextTimestampRaw === "string"
            ? parseInt(nextTimestampRaw, 10)
            : nextTimestampRaw;

        if (typeof nextTimestamp === "number" && !isNaN(nextTimestamp)) {
          lastWonTime = keepaTimeToUnixDateTime(nextTimestamp);
        }
      }
      break;
    }
  }

  if (lastWonTime === 0) {
    return new Date(0); // Never won
  }

  return new Date(lastWonTime);
}

/**
 * Get shipping timestamps that fall within a date range
 * Matches Selleramp's deriveSellerShippingArray logic
 */
function getShippingTimesInRange(
  buyBoxShippingTime: number[],
  startHistoryDate: number,
  endHistoryDate: number,
): number[] {
  // Find first timestamp after startHistoryDate
  const historyStartIndex = buyBoxShippingTime.findIndex(
    (item) => item > startHistoryDate,
  );
  const shippingStartIndex = historyStartIndex - 1;

  // Find first timestamp after endHistoryDate
  const historyEndIndex = buyBoxShippingTime.findIndex(
    (item) => item > endHistoryDate,
  );
  const shippingEndIndex =
    historyEndIndex < 0 ? buyBoxShippingTime.length : historyEndIndex;

  return buyBoxShippingTime.slice(shippingStartIndex, shippingEndIndex);
}

/**
 * Calculate average price for a SPECIFIC seller based on when they held the buy box
 * CSV format: [timestamp, price, shipping, timestamp, price, shipping, ...]
 * Matches Selleramp's deriveAvgPriceData logic
 */
function calculateAvgPriceForSeller(
  sellerId: string,
  buyBoxHistory: (number | string)[],
  csvBuyBox: number[],
  rangeStart: number,
  lastUpdate: number,
): number | null {
  if (!csvBuyBox || csvBuyBox.length < 3) return null;

  // Extract timestamps from csv (every 3rd element starting at 0)
  const buyBoxShippingTime: number[] = [];
  for (let i = 0; i < csvBuyBox.length; i += 3) {
    const timestampRaw = csvBuyBox[i];
    const timestamp =
      typeof timestampRaw === "string"
        ? parseInt(timestampRaw, 10)
        : timestampRaw;
    if (typeof timestamp === "number" && !isNaN(timestamp)) {
      buyBoxShippingTime.push(keepaTimeToUnixDateTime(timestamp));
    }
  }

  let totalAvgPrice = 0;
  let totalTime = 0;

  // Iterate through buyBoxHistory to find when this seller had the buy box
  for (let i = 1; i < buyBoxHistory.length; i += 2) {
    const currentSellerId = buyBoxHistory[i];
    if (currentSellerId !== sellerId) continue;

    // Get timestamps for this period
    const prevTimestampRaw = buyBoxHistory[i - 1];
    const nextTimestampRaw = buyBoxHistory[i + 1];

    const prevTimestamp =
      typeof prevTimestampRaw === "string"
        ? parseInt(prevTimestampRaw, 10)
        : prevTimestampRaw;
    const nextTimestamp =
      typeof nextTimestampRaw === "string"
        ? parseInt(nextTimestampRaw, 10)
        : nextTimestampRaw;

    if (typeof prevTimestamp !== "number" || isNaN(prevTimestamp)) continue;

    // Calculate start and end times for this period
    const isFirstIndex = i === 1;
    const isLastIndex = i === buyBoxHistory.length - 1;

    let startHistoryDate = isFirstIndex
      ? rangeStart
      : keepaTimeToUnixDateTime(prevTimestamp);

    let endHistoryDate = isLastIndex
      ? lastUpdate
      : typeof nextTimestamp === "number" && !isNaN(nextTimestamp)
        ? keepaTimeToUnixDateTime(nextTimestamp)
        : lastUpdate;

    // Handle edge case
    if (startHistoryDate === endHistoryDate) {
      endHistoryDate = Date.now();
    }

    // Get shipping times within this seller's period
    const sellerShippingArray = getShippingTimesInRange(
      buyBoxShippingTime,
      startHistoryDate,
      endHistoryDate,
    );

    // Calculate weighted price for this seller's period
    const lastShippingIndex = sellerShippingArray.length - 1;
    sellerShippingArray.forEach((shippingTime, idx) => {
      // Calculate duration for this price point
      let time: number;
      if (idx === lastShippingIndex) {
        const startDate =
          shippingTime > startHistoryDate ? shippingTime : startHistoryDate;
        time = endHistoryDate - startDate;
      } else if (idx === 0) {
        time = sellerShippingArray[1] - startHistoryDate;
      } else {
        time = sellerShippingArray[idx + 1] - shippingTime;
      }

      // Find this timestamp in the shipping CSV to get price
      const shippingTimeIndex = buyBoxShippingTime.indexOf(shippingTime);
      if (shippingTimeIndex >= 0) {
        // Map back to original CSV index
        const csvIndex = shippingTimeIndex * 3;
        const productPrice = csvBuyBox[csvIndex + 1] || 0;
        // Selleramp compares ONLY product price (no shipping)
        const price = productPrice;

        if (price > 0 && time > 0) {
          totalAvgPrice += price * time;
          totalTime += time;
        }
      }
    });
  }

  if (totalTime === 0) return null;

  const avgPriceCents = totalAvgPrice / totalTime;
  const avgPriceDollars = avgPriceCents / 100;
  return Math.round((avgPriceDollars + Number.EPSILON) * 100) / 100;
}

/**
 * Get stock count from offers
 * Special IDs return 0
 */
function getStock(offers: TOffer[], sellerId: string): number {
  // Special IDs don't have stock
  if (sellerId === SUPPRESSED_ID || sellerId === OUT_OF_STOCK_ID) {
    return 0;
  }
  const offer = offers.find((o) => o.sellerId === sellerId);
  return offer?.stock ?? 0;
}

/**
 * Transform Keepa time for CSV arrays
 * Converts Keepa timestamps to Unix milliseconds
 */
function transformKeepaTimeCsv(csvArray: number[], step: number = 2): number[] {
  if (!csvArray || csvArray.length === 0) return [];

  const result: number[] = [];
  for (let i = 0; i < csvArray.length; i++) {
    if (i % step === 0) {
      // It's a timestamp
      result.push(keepaTimeToUnixDateTime(csvArray[i]));
    } else {
      // It's a value (price)
      result.push(csvArray[i]);
    }
  }
  return result;
}

/**
 * Filter fulfillment type array to get values within a time range
 * Matches Selleramp's deriveFilteredFTArray logic
 */
function filterFTArrayByRange(
  fulfilmentTypeArray: number[],
  rangeStartDate: number,
  rangeEndDate: number,
): number[] {
  if (!fulfilmentTypeArray || fulfilmentTypeArray.length === 0) {
    return [];
  }

  const arrayLength = fulfilmentTypeArray.length;
  const result: number[] = [];

  for (let index = 0; index < arrayLength; index++) {
    const isTimeItem = index % 2 === 0;
    let lastDistance = 1;
    let typeTime = fulfilmentTypeArray[index - 1];
    let shiftToNextTimeValue = 1;

    if (isTimeItem) {
      lastDistance = 2;
      typeTime = fulfilmentTypeArray[index];
      shiftToNextTimeValue = 2;
    }

    const nextTypeTime = fulfilmentTypeArray[index + shiftToNextTimeValue];
    const lastIndex = arrayLength - lastDistance;
    const isLastPair = index === lastIndex;

    if (isLastPair) {
      if (typeTime < rangeEndDate) {
        result.push(fulfilmentTypeArray[index]);
      }
    } else {
      if (nextTypeTime > rangeStartDate && typeTime < rangeEndDate) {
        result.push(fulfilmentTypeArray[index]);
      }
    }
  }

  return result;
}

/**
 * Build fulfillment type data for all sellers
 * Matches Selleramp's deriveTypeData logic
 */
function buildFulfillmentTypeData(
  buyBoxHistory: (number | string)[],
  csvBuyBox: number[],
  csvFBA: number[],
  csvFBM: number[],
  sellerIds: string[],
  rangeStart: number,
  lastUpdate: number,
): Record<string, string[]> {
  const buyBoxTypeData: Record<string, string[]> = {};

  // Initialize empty arrays for each seller
  for (const sellerId of sellerIds) {
    buyBoxTypeData[sellerId] = [];
  }

  // Extract timestamps from csvBuyBox
  const buyBoxShippingTime: number[] = [];
  for (let i = 0; i < csvBuyBox.length; i += 3) {
    const timestampRaw = csvBuyBox[i];
    const timestamp =
      typeof timestampRaw === "string"
        ? parseInt(timestampRaw, 10)
        : timestampRaw;
    if (typeof timestamp === "number" && !isNaN(timestamp)) {
      buyBoxShippingTime.push(keepaTimeToUnixDateTime(timestamp));
    }
  }

  // Transform CSV arrays to Unix time
  const csvFBATransformed = transformKeepaTimeCsv(csvFBA, 2);
  const csvFBMShippingTransformed = transformKeepaTimeCsv(csvFBM, 3);

  // Remove shipping from FBM (Selleramp line 101921)
  // Filter out every 3rd element: [time, price, shipping] -> [time, price]
  const csvFBMTransformed: number[] = [];
  for (let i = 0; i < csvFBMShippingTransformed.length; i++) {
    if ((i + 1) % 3 !== 0) {
      // Keep time and price, skip shipping
      csvFBMTransformed.push(csvFBMShippingTransformed[i]);
    }
  }

  // Iterate through buyBoxHistory to fill type data
  for (let i = 1; i < buyBoxHistory.length; i += 2) {
    const sellerId = String(buyBoxHistory[i]);

    // Skip seller IDs that aren't in the filtered list
    if (!sellerIds.includes(sellerId)) {
      continue;
    }

    // Get timestamps for this period
    const prevTimestampRaw = buyBoxHistory[i - 1];
    const nextTimestampRaw = buyBoxHistory[i + 1];

    const prevTimestamp =
      typeof prevTimestampRaw === "string"
        ? parseInt(prevTimestampRaw, 10)
        : prevTimestampRaw;
    const nextTimestamp =
      typeof nextTimestampRaw === "string"
        ? parseInt(nextTimestampRaw, 10)
        : nextTimestampRaw;

    if (typeof prevTimestamp !== "number" || isNaN(prevTimestamp)) continue;

    // Calculate start and end times
    const isFirstIndex = i === 1;
    const isLastIndex = i === buyBoxHistory.length - 1;

    let startHistoryDate = isFirstIndex
      ? rangeStart
      : keepaTimeToUnixDateTime(prevTimestamp);

    let endHistoryDate = isLastIndex
      ? lastUpdate
      : typeof nextTimestamp === "number" && !isNaN(nextTimestamp)
        ? keepaTimeToUnixDateTime(nextTimestamp)
        : lastUpdate;

    if (startHistoryDate === endHistoryDate) {
      endHistoryDate = Date.now();
    }

    // Get shipping times within this seller's period
    const sellerShippingArray = getShippingTimesInRange(
      buyBoxShippingTime,
      startHistoryDate,
      endHistoryDate,
    );

    const lastShippingIndex = sellerShippingArray.length - 1;

    // Check each price point to determine type
    sellerShippingArray.forEach((shippingTime, idx) => {
      // Find this timestamp in the shipping CSV to get price
      const shippingTimeIndex = buyBoxShippingTime.indexOf(shippingTime);
      if (shippingTimeIndex >= 0) {
        const csvIndex = shippingTimeIndex * 3;
        const price = csvBuyBox[csvIndex + 1] || 0;

        // Determine range for this price point
        let rangeEndDate: number;
        if (idx === lastShippingIndex) {
          rangeEndDate = endHistoryDate;
        } else {
          rangeEndDate = sellerShippingArray[idx + 1];
        }

        // Filter FBM and FBA arrays to this time range
        // Both are now [time, price, time, price, ...] format (shipping already removed from FBM)
        const filteredCsvFBM = filterFTArrayByRange(
          csvFBMTransformed,
          shippingTime,
          rangeEndDate,
        );
        const filteredCsvFBA = filterFTArrayByRange(
          csvFBATransformed,
          shippingTime,
          rangeEndDate,
        );

        // Check if this price exists in FBM or FBA during this time
        // Selleramp line 102110-102114
        if (filteredCsvFBM.includes(price)) {
          buyBoxTypeData[sellerId].push("FBM");
        }
        if (filteredCsvFBA.includes(price)) {
          buyBoxTypeData[sellerId].push("FBA");
        }
      }
    });
  }

  return buyBoxTypeData;
}

/**
 * Calculate fulfillment type for a seller
 * Matches Selleramp's calculateFType logic
 */
function calculateFulfillmentType(
  sellerId: string,
  sellerName: string,
  buyBoxTypeData: Record<string, string[]>,
): string {
  // Special IDs return "-"
  if (sellerId === SUPPRESSED_ID || sellerId === OUT_OF_STOCK_ID) {
    return "-";
  }

  const sellerTypes = buyBoxTypeData[sellerId] || [];
  const hasFBA = sellerTypes.includes("FBA");
  const hasFBM = sellerTypes.includes("FBM");

  // Check if this is Amazon
  const amazonSellers = ["Amazon", "Amazon.com", "ATVPDKIKX0DER"];
  const isAmazon =
    amazonSellers.includes(sellerName) || sellerId === "ATVPDKIKX0DER";

  if (hasFBA && isAmazon) {
    return "AMZ";
  }
  if (hasFBA) {
    return "FBA";
  }
  if (hasFBM) {
    return "FBM";
  }

  // Selleramp line 102223 - default to FBM if nothing detected!
  return "FBM";
}

/**
 * Generate Amazon seller profile link
 * Special IDs return empty string
 */
function generateSellerLink(sellerId: string): string {
  // Special IDs don't have links
  if (sellerId === SUPPRESSED_ID || sellerId === OUT_OF_STOCK_ID) {
    return "";
  }
  return `https://www.amazon.com/sp?seller=${sellerId}`;
}

/**
 * Filter history by date range
 * Uses alternating format: [time, sellerId, time, sellerId, ...]
 * Matches Selleramp's filterSellersByRange logic
 */
function filterHistoryByRange(
  buyBoxHistory: (number | string)[],
  rangeStart: number,
): (number | string)[] {
  if (buyBoxHistory.length === 0) {
    return buyBoxHistory;
  }

  // Find first timestamp after rangeStart
  // Timestamps can be numbers or strings, so we need to convert
  const foundIndex = buyBoxHistory.findIndex((item, index) => {
    // Only check even indices (timestamps)
    if (index % 2 !== 0) return false;

    // Convert to number if it's a string
    const timestamp = typeof item === "string" ? parseInt(item, 10) : item;
    if (isNaN(timestamp)) return false;

    const itemTime = keepaTimeToUnixDateTime(timestamp);
    return itemTime > rangeStart;
  });

  // If no timestamp found after rangeStart, return last 2 items
  if (foundIndex < 0) {
    const startIndex = Math.max(0, buyBoxHistory.length - 2);
    return buyBoxHistory.slice(startIndex);
  }

  // If found at first position, return all
  if (foundIndex === 0) {
    return buyBoxHistory;
  }

  // Shift back 2 positions to get complete [time, sellerId] pair
  const startIndex = foundIndex - 2;
  return buyBoxHistory.slice(startIndex);
}

// ============================================
// MAIN TRANSFORMATION FUNCTION
// ============================================

/**
 * Transform Keepa data to buy box analysis table data
 * Compatible with Sellerscout project structure
 *
 * @param keepaData - Keepa data in Sellerscout format
 * @param rangeDays - Number of days to analyze (default: 30)
 * @returns Array of buy box analysis data
 */
export function transformKeepaToBuyBoxAnalysis(
  keepaData: KeepaData,
  rangeDays: number = 30,
): TBuyBoxAnalysis[] {
  if (!keepaData.buyBoxSellerIdHistory || !keepaData.csv) {
    return [];
  }

  const offers = keepaData.offers || [];
  let buyBoxHistory = keepaData.buyBoxSellerIdHistory;

  // Use current date if lastUpdate is not provided
  const lastUpdate =
    keepaData.lastUpdate || unixDateTimeToKeepaTime(Date.now());
  const now = keepaTimeToUnixDateTime(lastUpdate);

  // Calculate range start
  let rangeStart = now - rangeDays * 24 * 60 * 60 * 1000;

  // Filter by range if specified (Selleramp's logic)
  if (rangeDays && rangeDays > 0) {
    buyBoxHistory = filterHistoryByRange(buyBoxHistory, rangeStart);
  } else {
    // "All" range - use first timestamp as rangeStart (Selleramp line 101936)
    if (buyBoxHistory.length > 0) {
      const firstTimestampRaw = buyBoxHistory[0];
      const firstTimestamp =
        typeof firstTimestampRaw === "string"
          ? parseInt(firstTimestampRaw, 10)
          : firstTimestampRaw;

      if (typeof firstTimestamp === "number" && !isNaN(firstTimestamp)) {
        rangeStart = keepaTimeToUnixDateTime(firstTimestamp);
      }
      // Don't filter for "All" range - use the whole history
    }
  }

  // Get buy box CSV data (index 18)
  const csvBuyBox =
    keepaData.csv[KeepaCSV.BUY_BOX_SHIPPING] || keepaData.csv[0] || [];

  // Get FBA and FBM CSV data for fulfillment type calculation
  const csvFBA = keepaData.csv[KeepaCSV.NEW_FBA] || [];
  const csvFBM = keepaData.csv[KeepaCSV.NEW_FBM_SHIPPING] || [];

  // Extract unique seller IDs (excluding special/invalid IDs like -1, -2, -)
  // Filter to only include sellers with last won date in past 365 days
  const sellerIds = extractSellerIds(buyBoxHistory, lastUpdate);

  // Build fulfillment type data for all sellers (Selleramp's deriveTypeData)
  const buyBoxTypeData = buildFulfillmentTypeData(
    buyBoxHistory,
    csvBuyBox,
    csvFBA,
    csvFBM,
    sellerIds,
    rangeStart,
    now,
  );

  // Transform to table data (NO filtering - include all sellers like Selleramp does)
  const tableData = sellerIds
    .map((sellerId): TBuyBoxAnalysis | null => {
      const percentWon = calculatePercentWon(
        buyBoxHistory,
        sellerId,
        rangeStart,
        now,
      );

      const stock = getStock(offers, sellerId);
      const lastWonDate = calculateLastWon(buyBoxHistory, sellerId, lastUpdate);

      // Calculate average price for THIS seller (seller-specific weighted average)
      const avgPrice = calculateAvgPriceForSeller(
        sellerId,
        buyBoxHistory,
        csvBuyBox,
        rangeStart,
        now,
      );

      // Calculate fulfillment type using price matching (Selleramp's logic)
      const sellerName = getSellerDisplayName(sellerId);
      const fType = calculateFulfillmentType(
        sellerId,
        sellerName,
        buyBoxTypeData,
      );

      // For special IDs, avgPrice should be 0 or handled specially
      const isSpecialId =
        sellerId === SUPPRESSED_ID || sellerId === OUT_OF_STOCK_ID;
      const finalAvgPrice = isSpecialId ? 0 : (avgPrice ?? 0);

      return {
        sellerId,
        sellerName, // Use the sellerName we already calculated
        avgPrice: finalAvgPrice,
        won: percentWon,
        lastWon: lastWonDate,
        stock,
        type: fType,
        link: generateSellerLink(sellerId),
      };
    })
    .filter((item): item is TBuyBoxAnalysis => item !== null)
    .sort((a, b) => b.won - a.won); // Sort by percentage won (descending)

  return tableData;
}

// ============================================
// ADVANCED VERSION WITH MORE FEATURES
// ============================================

/**
 * Extended analysis with additional metrics
 */
export interface TBuyBoxAnalysisExtended extends TBuyBoxAnalysis {
  rating?: number;
  ratingCount?: number;
  minPrice?: number;
  maxPrice?: number;
  priceChanges?: number;
}

/**
 * Transform with extended information
 */
export function transformKeepaToBuyBoxAnalysisExtended(
  keepaData: KeepaData,
  rangeDays: number = 30,
): TBuyBoxAnalysisExtended[] {
  const basicAnalysis = transformKeepaToBuyBoxAnalysis(keepaData, rangeDays);

  // Get CSV data for additional metrics
  const csvBuyBox =
    keepaData.csv[KeepaCSV.BUY_BOX_SHIPPING] || keepaData.csv[0] || [];
  const includeShipping = csvBuyBox.length > 0 && csvBuyBox.length % 3 === 0;
  const step = includeShipping ? 3 : 2;

  // Calculate price statistics
  const prices: number[] = [];
  for (let i = 1; i < csvBuyBox.length; i += step) {
    const price = csvBuyBox[i];
    const shipping = includeShipping && csvBuyBox[i + 1] ? csvBuyBox[i + 1] : 0;
    if (price > 0) {
      prices.push((price + shipping) / 100);
    }
  }

  const minPrice = prices.length > 0 ? Math.min(...prices) : undefined;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : undefined;
  const priceChanges = prices.length - 1;

  return basicAnalysis.map((item) => ({
    ...item,
    minPrice,
    maxPrice,
    priceChanges,
    // Additional fields would come from API_GET_SELLERS in real implementation
    sellerName: item.sellerId === "ATVPDKIKX0DER" ? "Amazon.com" : undefined,
  }));
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
  return value < 1 ? "<1%" : `${Math.round(value)}%`;
}

/**
 * Format last won time for display
 */
export function formatLastWon(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return date.toLocaleDateString();
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

/**
 * Sort analysis by percentage won (descending)
 */
export function sortByPercentWon(
  analysis: TBuyBoxAnalysis[],
): TBuyBoxAnalysis[] {
  return [...analysis].sort((a, b) => b.won - a.won);
}

/**
 * Filter out special seller IDs (suppressed, out of stock, invalid)
 */
export function filterSpecialSellers(
  analysis: TBuyBoxAnalysis[],
): TBuyBoxAnalysis[] {
  return analysis.filter((item) => isValidSellerId(item.sellerId));
}
